// src/Component/chat/component/ChatListing.tsx
import { Badge } from "flowbite-react";
import { useContext, useMemo, useEffect, useRef, useState } from "react";
import { Icon } from "@iconify/react";
import { 
  formatDistanceToNowStrict, 
  isToday, 
  isYesterday, 
  isThisWeek,
  format 
} from "date-fns";
import SimpleBar from "simplebar-react";
import { ChatContext } from "../../../context/ChatContext/chatContext";
import React from "react";
import { ChatsType } from "../../../types/chat/chat";
import { doc, onSnapshot } from 'firebase/firestore';
import { db } from '../../../config/firebase';
import { FIREBASE_COLLECTIONS } from '../../api/url';

// ── Skeleton loader ─────────────────────────────────────────────────────────

const ChatItemSkeleton = () => (
  <div className="py-4 px-5 flex items-center justify-between animate-pulse">
    <div className="flex items-center gap-3 flex-1 min-w-0">
      <div className="relative flex-shrink-0">
        <div className="rounded-full sm:h-12 sm:w-12 h-9 w-9 bg-gray-200" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="h-4 bg-gray-200 rounded w-3/4 mb-2" />
        <div className="h-3 bg-gray-200 rounded w-1/2" />
      </div>
    </div>
    <div className="flex flex-col items-end gap-1 flex-shrink-0 ml-2">
      <div className="h-3 bg-gray-200 rounded w-12" />
    </div>
  </div>
);

// ── Component ───────────────────────────────────────────────────────────────

const ChatListing = () => {
  const {
    chatData,
    chatSearch,
    setChatSearch,
    setSelectedChat,
    setActiveChatId,
    activeChatId,
    loading,
    messagesLoading,
    getUnreadCount,
  } = useContext(ChatContext);

  const [userOnlineStatus, setUserOnlineStatus] = useState<{ [chatId: string]: string }>({});
  const activeItemRef = useRef<HTMLDivElement | null>(null);

  // Scroll the active chat item into view whenever the selection changes
  useEffect(() => {
    if (!activeChatId || !activeItemRef.current) return;
    activeItemRef.current.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  }, [activeChatId]);

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setChatSearch(e.target.value);
  };

  // Subscribe to client online status for all chats
  useEffect(() => {
    const unsubscribes: (() => void)[] = [];

    chatData.forEach((chat) => {
      const chatId = chat.id as string;
      if (!chatId) return;

      // Subscribe to the user doc stored under the USERS collection
      const userDocRef = doc(db, FIREBASE_COLLECTIONS.USERS, chatId);
      const unsubscribe = onSnapshot(
        userDocRef,
        (docSnapshot) => {
          if (docSnapshot.exists()) {
            const status = docSnapshot.data()?.onlineStatus || 'offline';
            setUserOnlineStatus((prev) => ({ ...prev, [chatId]: status }));
          }
        },
        () => {} // swallow errors silently for listing
      );

      unsubscribes.push(unsubscribe);
    });

    return () => unsubscribes.forEach((unsub) => unsub());
  }, [chatData]);

  const getUserInitials = (name: string): string => {
    if (!name) return 'U';
    const parts = name.trim().split(' ');
    if (parts.length >= 2) {
      return parts[0].charAt(0).toUpperCase() + parts[parts.length - 1].charAt(0).toUpperCase();
    }
    return parts[0].charAt(0).toUpperCase();
  };

  const getLastActivity = (chat: ChatsType): Date | null => {
    if (chat.lastMessageAt) return new Date(chat.lastMessageAt);
    if (chat.messages?.length) {
      const last = chat.messages[chat.messages.length - 1];
      if (last?.createdAt) return new Date(last.createdAt);
    }
    return null;
  };

  const getLatestMessageText = (chat: ChatsType): string => {
    if (chat.messages?.length) {
      const last = chat.messages[chat.messages.length - 1];
      if (last) {
        if (last.type === 'image') return '📷 Sent a photo';
        if (last.type === 'file')  return `📎 Sent ${last.attachment?.length || 1} file(s)`;
        return last.msg || 'No message';
      }
    }
    return chat.excerpt || 'No messages yet';
  };

  const formatWhatsAppTime = (date: Date | null): string => {
    if (!date) return '';
    const d = new Date(date);
    if (isToday(d))    return format(d, 'HH:mm');
    if (isYesterday(d)) return 'Yesterday';
    if (isThisWeek(d, { weekStartsOn: 1 })) return format(d, 'EEEE');
    return format(d, 'dd/MM/yyyy');
  };

  const getStatusBadge = (status: string) => {
    const colorMap: Record<string, string> = {
      online: 'success',
      busy:   'failure',
      away:   'warning',
    };
    return (
      <Badge
        color={colorMap[status] || 'gray'}
        className="p-0 h-2.5 w-2.5 absolute bottom-0 right-0 ring-2 ring-white"
      />
    );
  };

  const filteredAndSortedChats = useMemo(() => {
    const filtered = chatData.filter((chat) =>
      chat.name.toLowerCase().includes(chatSearch.toLowerCase())
    );

    return filtered.sort((a, b) => {
      const aTime = getLastActivity(a);
      const bTime = getLastActivity(b);
      if (aTime && bTime) return bTime.getTime() - aTime.getTime();
      if (aTime) return -1;
      if (bTime) return  1;
      return 0;
    });
  }, [chatData, chatSearch]);

  const handleChatSelect = (chat: ChatsType) => {
    setSelectedChat(chat);
    setActiveChatId(chat.id as string);
  };

  return (
    <div className="w-full h-full flex flex-col">
      {/* Search */}
      <div className="px-4 py-4 flex-shrink-0">
        <div className="relative w-full">
          <Icon
            icon="solar:magnifer-line-duotone"
            height={18}
            className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"
          />
          <input
            id="search"
            type="text"
            placeholder="Search contacts"
            onChange={handleSearchChange}
            value={chatSearch}
            className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-[6px] bg-white text-gray-900 placeholder-gray-400 focus:outline-none"
            disabled={loading}
          />
        </div>
      </div>

      {/* Heading */}
      <div className="flex-shrink-0">
        <h3 className="bg-white font-inter font-medium text-[14px] px-4 pb-2">Recent Chats</h3>
      </div>

      {/* List */}
      <div className="flex-1 overflow-hidden pb-4">
        {loading ? (
          <SimpleBar style={{ height: '100%' }} autoHide={false}>
            {[...Array(5)].map((_, i) => <ChatItemSkeleton key={i} />)}
          </SimpleBar>
        ) : (
          <SimpleBar style={{ height: '100%' }} autoHide={false}>
            {filteredAndSortedChats.length > 0 ? (
              filteredAndSortedChats.map((chat) => {
                const lastActivity    = getLastActivity(chat);
                const latestMessage   = getLatestMessageText(chat);
                const isActive        = activeChatId === chat.id;
                const formattedTime   = formatWhatsAppTime(lastActivity);
                const currentStatus   = userOnlineStatus[chat.id as string] || chat.status;
                const unreadCount     = getUnreadCount(chat.id as string);

                return (
                  <div
                    key={chat.id}
                    ref={isActive ? activeItemRef : null}
                    className={`cursor-pointer py-4 px-5 flex items-center justify-between group transition-all duration-200 border-l-4 ${
                      isActive ? 'bg-[#F1FBFB] border-l-[#00A9A9]' : 'hover:bg-gray-50 border-l-transparent'
                    }`}
                    onClick={() => handleChatSelect(chat)}
                  >
                    <div className="flex items-center gap-3 flex-1 min-w-0">
                      {/* Avatar */}
                      <div className="relative flex-shrink-0">
                        {chat.thumb && chat.thumb.trim() !== '' ? (
                          <img
                            src={chat.thumb}
                            height={48} width={48}
                            alt={chat.name}
                            className="rounded-full sm:h-12 sm:w-12 h-9 w-9 object-cover"
                            onError={(e) => {
                              e.currentTarget.style.display = 'none';
                              const fallback = e.currentTarget.nextElementSibling as HTMLElement;
                              if (fallback) fallback.style.display = 'flex';
                            }}
                          />
                        ) : null}
                        <div
                          className="rounded-full sm:h-12 sm:w-12 h-9 w-9 bg-[#DAF9F9] items-center justify-center flex-shrink-0"
                          style={{ display: chat.thumb && chat.thumb.trim() !== '' ? 'none' : 'flex' }}
                        >
                          <span className="sm:text-lg text-base font-semibold text-[#00A9A9]">
                            {getUserInitials(chat.name)}
                          </span>
                        </div>
                        {getStatusBadge(currentStatus)}
                      </div>

                      {/* Name + excerpt */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <h5 className={`text-sm mb-1 font-inter truncate transition-all duration-200 ${
                            unreadCount > 0 ? 'font-bold text-gray-900' : isActive ? 'font-semibold text-gray-900' : 'font-medium text-gray-700'
                          }`}>
                            {chat.name}
                          </h5>
                          {messagesLoading[chat.id as string] && (
                            <Icon icon="solar:loading-outline" height={14} className="text-gray-400 animate-spin flex-shrink-0" />
                          )}
                        </div>
                        <div className={`text-xs font-inter truncate transition-all duration-200 ${
                          unreadCount > 0 ? 'text-gray-900 font-semibold' : isActive ? 'text-gray-700 opacity-90' : 'text-ld opacity-75'
                        }`}>
                          {latestMessage}
                        </div>
                      </div>
                    </div>

                    {/* Time + unread badge */}
                    <div className="flex flex-col items-end gap-1 flex-shrink-0 ml-2">
                      <div className={`text-xs transition-all duration-200 ${
                        unreadCount > 0 ? 'text-[#00A9A9]' : isActive ? 'text-primary' : 'text-ld opacity-60'
                      }`}>
                        {formattedTime}
                      </div>
                      {unreadCount > 0 && (
                        <div className="bg-[#00A9A9] text-white text-xs font-semibold rounded-full min-w-[20px] h-[20px] flex items-center justify-center px-1.5">
                          {unreadCount > 99 ? '99+' : unreadCount}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })
            ) : (
              <div className="flex items-center justify-center h-full p-5">
                <div className="text-center">
                  <Icon icon="solar:chat-line-linear" height={60} className="mx-auto mb-3 text-gray-400" />
                  <p className="text-sm text-gray-500">No chats found</p>
                </div>
              </div>
            )}
          </SimpleBar>
        )}
      </div>
    </div>
  );
};

export default ChatListing;